import React, { useState, useEffect, useRef, useMemo, useCallback } from "react";
import {
  DataTable,
  TableContainer,
  TableToolbar,
  TableToolbarContent,
  TableToolbarSearch,
  Table,
  TableHead,
  TableRow,
  TableHeader,
  TableBody,
  TableCell,
  Pagination,
  InlineLoading,
  InlineNotification,
  Tag,
  Button
} from "@carbon/react";
import { DatePicker, DatePickerInput } from "@carbon/react";
import { callApi } from "../../../services/apiService";


// Define table headers for verification report
const headers = [
  { key: "propertySeqId", header: "Property ID" },
  { key: "propertyTitle", header: "Property Title" },
  { key: "propertyType", header: "Property Type" },
  { key: "verificationStatus", header: "Verification Status" },
  { key: "daysSinceCreation", header: "Since Created" },
  { key: "daysInCurrentStatus", header: "In Current Status" },
  { key: "createdTime", header: "Created At" },
  { key: "status", header: "Status" },
];

const formatDateForApi = (date) => {
  if (!date) return "";

  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");

  return `${year}-${month}-${day}`;
};


// Helper function to get status tag type
const getStatusTagType = (status = "") => {
  const value = status.toLowerCase().replace(/_/g, " ");

  switch (value) {
    case "draft":
      return "purple";

    case "pending":
      return "red";

    case "Under_Verification":
      return "yellow";
      
    case "Active".toLowerCase():
      return "green";

    default:
      return "cool-gray";
  }
};

export default function VerificationReport() {
  const mountedRef = useRef(true);
  const [rows, setRows] = useState([]);

  // Filter states
  const [filters, ] = useState({
    propertyType: [],
    verificationStatus: [],
    status: []
  });

const [dateRange, setDateRange] = useState({
  from: null,
  to: null,
});
  // Filter options
  const [ setFilterOptions] = useState({
    propertyType: [],
    verificationStatus: [],
    status: []
  });


  // Loading and error states
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  // Pagination and Search states
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(100);
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");


  // ------------------ Search & Filter Logic ------------------
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(searchTerm.trim().toLowerCase()), 350);
    return () => clearTimeout(t);
  }, [searchTerm]);

  // Extract unique values for filters from fetched data
  const extractUniqueValues = useCallback((data) => {
    const uniquePropertyTypes = Array.from(
      new Set(data.map(item => item.property_type).filter(Boolean))
    ).map(type => ({ id: type, text: type }));
    
    const uniqueVerificationStatus = Array.from(
      new Set(data.map(item => item.verification_status).filter(Boolean))
    ).map(status => ({ id: status, text: status }));
    
    const uniqueStatus = Array.from(
      new Set(data.map(item => item.status).filter(Boolean))
    ).map(status => ({ id: status, text: status }));
    
    return {
      propertyType: uniquePropertyTypes,
      verificationStatus: uniqueVerificationStatus,
      status: uniqueStatus
    };
  }, []);

  // Apply filters
const applyFilters = useCallback((data) => {
  if (!data || data.length === 0) return data;

  return data.filter((row) => {

    // Filter by property type
    if (filters.propertyType.length > 0) {
      const hasMatchingType = filters.propertyType.some(
        (filter) => filter.text === row.propertyType
      );

      if (!hasMatchingType) return false;
    }

    // Filter by verification status
    if (filters.verificationStatus.length > 0) {
      const hasMatchingVerification = filters.verificationStatus.some(
        (filter) => filter.text === row.verificationStatus
      );

      if (!hasMatchingVerification) return false;
    }

    // Filter by status
    if (filters.status.length > 0) {
      const hasMatchingStatus = filters.status.some(
        (filter) => filter.text === row.status
      );

      if (!hasMatchingStatus) return false;
    }

    return true;
  });
}, [filters]);

  // Normalize Verification Report Data
  const normalizeVerificationData = useCallback((item) => ({
    id: item.property_id,
    propertyUuid: item.property_uuid,
    propertySeqId: item.property_seq_id,
    propertyTitle: item.property_title,
    propertyType: item.property_type,
    verificationStatus: item.verification_status,
    status: item.status,
createdTimestamp: item.created_timestamp, 

createdTime: item.created_timestamp
  ? new Date(item.created_timestamp).toLocaleString()
  : "-",
    updatedTimestamp: item.updated_timestamp,
    verifiedDateTime: item.verified_date_time,
    verifiedByUserId: item.verified_by_user_id,
    verifiedByName: item.verified_by_name || "-",
    createdByUserId: item.created_by_user_id,
    createdByName: item.created_by_name,
    daysInStatus: item.days_in_status?.toFixed(2) || "0",
    hoursInStatus: item.hours_in_status?.toFixed(2) || "0",
    daysSinceCreation: item.days_since_creation_display || "-",
    daysInCurrentStatus: item.days_in_current_status_display || "-",
  }), []);

  // Fetch Verification Report Data
  const fetchVerificationReport = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const response = await callApi("GET", "/properties/report");
      const data = Array.isArray(response?.data?.properties) ? response.data.properties : [];
        // const apiData = response?.data?.verified_properties || [];
        // const data = Array.isArray(apiData) ? apiData : [];
      
      const normalizedData = data.map(normalizeVerificationData);
      setRows(normalizedData);
      
      // Update filter options based on fetched data
      const uniqueValues = extractUniqueValues(data);
      setFilterOptions(uniqueValues);
      
      return normalizedData;
    } catch (error) {
      
     // setError("Failed to load verification report. Please try again.");
      return [];
    } finally {
      if (mountedRef.current) {
        setLoading(false);
      }
    }
  }, [normalizeVerificationData, extractUniqueValues, setFilterOptions]);

  // Load data on component mount
  useEffect(() => {
    mountedRef.current = true;
    fetchVerificationReport();
    
    return () => {
      mountedRef.current = false;
    };
  }, [fetchVerificationReport]);

// Filter rows based on search and filters
const filteredRows = useMemo(() => {
  let result = rows;

  // Search
  if (debouncedSearch) {
    result = result.filter((row) => {
      return (
        String(row.propertySeqId || "")
          .toLowerCase()
          .includes(debouncedSearch) ||

        String(row.propertyTitle || "")
          .toLowerCase()
          .includes(debouncedSearch) ||

        String(row.propertyType || "")
          .toLowerCase()
          .includes(debouncedSearch) ||

        String(row.verificationStatus || "")
          .toLowerCase()
          .includes(debouncedSearch) ||

        String(row.status || "")
          .toLowerCase()
          .includes(debouncedSearch)
      );
    });
  }

  // Apply other filters
  result = applyFilters(result);

  return result;
}, [rows, debouncedSearch, applyFilters]);


// Reset pagination when search changes
useEffect(() => {
  setPage(1);
}, [debouncedSearch]);


// Pagination
const paginatedRows = useMemo(
  () =>
    filteredRows.slice(
      (page - 1) * pageSize,
      page * pageSize
    ),
  [filteredRows, page, pageSize]
);

  

  // Count active filters
  const activeFilterCount = Object.values(filters).reduce((count, filter) => {
    if (Array.isArray(filter)) {
      return count + filter.length;
    } else if (filter && filter !== "") {
      return count + 1;
    }
    return count;
  }, 0);
  const handleSearch = async () => {
  setLoading(true);
  setError("");

  try {
    const params = {};

if (dateRange.from) {
  params.start_date = formatDateForApi(dateRange.from);
}

if (dateRange.to) {
  params.end_date = formatDateForApi(dateRange.to);
}



const response = await callApi(
  "GET",
  "/properties/report",
  params
);

const data = Array.isArray(response?.data?.properties)
  ? response.data.properties
  : [];

const normalizedData = data.map(normalizeVerificationData);

setRows(normalizedData);
setPage(1);
  } catch (err) {
    
  } finally {
    setLoading(false);
  }
}; 
  
  return (
    <>
<div
  style={{
    padding: "1rem",
    backgroundColor: "#f4f4f4",
    borderRadius: "8px",
    marginBottom: "1rem",
    border: "1px solid #e0e0e0",
  }}
>
  <div
    style={{
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      flexWrap: "wrap",
      gap: "1rem",
    }}
  >
    <div>
      <h4
        style={{
          margin: 0,
          marginBottom: "0.25rem",
          fontWeight: 600,
          color: "#161616",
        }}
      >
         Filters Verification 
      </h4>
      
    </div>

    <div
      style={{
        display: "flex",
        gap: "0.75rem",
        alignItems: "flex-end",
        flexWrap: "wrap",
      }}
    >
      <DatePicker
        key={`${dateRange.from}-${dateRange.to}`}
        datePickerType="range"
        onChange={(dates) => {
          setDateRange({
            from: dates[0] || null,
            to: dates[1] || null,
          });
        }}
        value={[
          dateRange.from || "",
          dateRange.to || "",
        ]}
      >
        <DatePickerInput
          id="date-picker-from"
          placeholder="From date"
          labelText=""
        />

        <DatePickerInput
          id="date-picker-to"
          placeholder="To date"
          labelText=""
        />
      </DatePicker>

      <Button
      kind="primary"
      size="sm"
      onClick={handleSearch}
      >
        Search
      </Button>

      <Button
  kind="secondary"
  size="sm"
  disabled={!dateRange.from && !dateRange.to}
  onClick={() => {
    setDateRange({
      from: null,
      to: null,
    });

    fetchVerificationReport();
  }}
>
  Clear
</Button>
    </div>
  </div>
</div>
      <DataTable rows={paginatedRows} headers={headers}>
        {({ rows, headers, getHeaderProps, getRowProps, getSelectionProps, getBatchActionProps, selectedRows }) => {
          
          return (
            
            <TableContainer title="Verification Pending Report" >
                
              <TableToolbar>
                <TableToolbarContent>
                  <TableToolbarSearch
                    expanded
                    onChange={(e) => setSearchTerm(e.target.value)}
                    placeholder="Search by property ID, title, type, Verification status..."
                  />
                  
                  {/* <Button
                    kind="primary"
                    renderIcon={Download}
                    onClick={handleExport}
                    size="sm"
                  >
                    Export
                  </Button> */}
                </TableToolbarContent>
              </TableToolbar>
               {/* Loading and Error Messages */}
              {loading && (
                <div style={{ padding: "2rem", textAlign: "center" }}>
                  <InlineLoading description="Loading verification report..." />
                </div>
              )}

              {error && !loading && (
                <div style={{ padding: "1rem" }}>
                  <InlineNotification
                    kind="error"
                    title="Error"
                    subtitle={error}
                    lowContrast
                    onClose={() => setError("")}
                  />
                </div>
              )}

              {success && (
                <div style={{ padding: "1rem" }}>
                  <InlineNotification
                    kind="success"
                    title="Success"
                    subtitle={success}
                    lowContrast
                    onClose={() => setSuccess("")}
                  />
                </div>
              )}

              <Table>
                <TableHead>
                  <TableRow>
                  {headers.map((header) => {
                    const { key, ...headerProps } = getHeaderProps({ header });

                    return (
                      <TableHeader key={key} {...headerProps}>
                        {header.header}
                      </TableHeader>
                    );
                  })}
                  </TableRow>
                </TableHead>

                <TableBody>
                  {!loading && paginatedRows.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={headers.length + 2} style={{ textAlign: 'center', padding: '2rem' }}>
                        {searchTerm || activeFilterCount > 0 
                          ? 'No results found. Try adjusting your search or filters.' 
                          : 'No verification data found.'}
                      </TableCell>
                    </TableRow>
                  ) : (
                    paginatedRows.map((row) => (
                      <TableRow key={row.id} {...getRowProps({ row })}>
                        <TableCell>{row.propertySeqId}</TableCell>
                        <TableCell>{row.propertyTitle}</TableCell>
                        <TableCell>{row.propertyType}</TableCell>
                        <TableCell>
                          <Tag type={getStatusTagType(row.verificationStatus)}>
                            {row.verificationStatus}
                          </Tag>
                        </TableCell><TableCell>{row.daysSinceCreation}</TableCell>
                        <TableCell>{row.daysInCurrentStatus}</TableCell>
                        <TableCell>{row.createdTime}</TableCell>
                        <TableCell>
                          <Tag type={getStatusTagType(row.status)}>
                            {row.status}
                          </Tag>
                        </TableCell>
                       
                       
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          );
        }}
      </DataTable>

      {/* Pagination */}
      {filteredRows.length > 0 && !loading && (
        <div style={{ padding: "1rem 1.25rem", display: "flex", justifyContent: "flex-end" }}>
          <Pagination
            totalItems={filteredRows.length}
            page={page}
            pageSize={pageSize}
            pageSizes={[100, 50, 20, 10, 5]}
            onChange={({ page: p, pageSize: ps }) => { 
              setPage(p); 
              setPageSize(ps); 
            }}
          />
        </div>
      )}
    </>
  );
}